import React, { useCallback } from 'react';
import MaterialTable from '@material-table/core';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { EsmeModel, EsmeObject } from '../../../../models/esmeModel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { Chip } from '@mui/material';
import { convertVersionableIdToString, VersionableId } from '../../../../types/versionableId';

interface Props {
  readonly esmeList: EsmeModel[];
  readonly isLoading: boolean;
  readonly onAddClick: () => void;
  readonly onViewClick: (esmeId: VersionableId) => void;
  readonly onEditClick: (esmeId: VersionableId) => void;
  readonly onDeleteClick: (esmeId: VersionableId) => void;
}

type EsmeRow = EsmeObject & {
  readonly id: string;
  readonly versionableId: VersionableId;
};

const EsmeTable = (props: Props) => {
  const {
    esmeList,
    isLoading,
    onAddClick,
    onViewClick,
    onEditClick,
    onDeleteClick,
  } = props;

  const esmeRows: EsmeRow[] = esmeList.map(esme => ({
    id: convertVersionableIdToString(esme.id),
    versionableId: esme.id,
    ...esme.object,
  }));

  const renderMtRoutesCount = useCallback((esmeRow: EsmeRow) => {
    return <Chip label={esmeRow.mtRoutes?.length || 0} />;
  }, []);

  const renderMoRoutesCount = useCallback((esmeRow: EsmeRow) => {
    return <Chip label={esmeRow.moRoutes?.length || 0} />;
  }, []);

  return (
    <MaterialTable
      title={
        <Typography component="span" color="textSecondary" gutterBottom>
          <Typography component="span" variant="h5">
            ESMEs
          </Typography>
        </Typography>
      }
      data={esmeRows}
      isLoading={isLoading}
      columns={[
        {
          title: 'Id',
          field: 'versionableId.resourceName',
        },
        {
          title: 'Username',
          field: 'user.username',
          defaultSort: 'asc',
        },
        {
          title: 'Log file',
          field: 'user.logFile',
        },
        {
          title: 'Log level',
          field: 'user.logLevel',
        },
        {
          title: 'MT route(s)',
          type: 'numeric',
          render: renderMtRoutesCount
        },
        {
          title: 'MO route(s)',
          type: 'numeric',
          render: renderMoRoutesCount,
        },
      ]}
      actions={[
        {
          icon: () => <AddBoxIcon color='primary' />,
          tooltip: 'Add esme',
          isFreeAction: true,
          onClick: () => onAddClick(),
        },
        esmeRow => ({
          icon: () => <VisibilityIcon color='primary' />,
          tooltip: 'View esme',
          onClick: () => onViewClick(esmeRow.versionableId),
        }),
        esmeRow => ({
          icon: () => <EditIcon color='primary' />,
          tooltip: 'Edit esme',
          onClick: () => onEditClick(esmeRow.versionableId),
        }),
        esmeRow => ({
          icon: () => <DeleteIcon color='primary' />,
          tooltip: 'Remove esme',
          onClick: () => onDeleteClick(esmeRow.versionableId),
        }),
      ]}
      options={{
        padding: 'dense',
        selection: false,
        pageSizeOptions: [10, 25, 50],
        pageSize: 10,
        actionsColumnIndex: 6,
        headerStyle: {
          fontWeight: 'bold',
        },
      }}
      components={{
        Container: props => <Paper elevation={1} {...props} />
      }}
    />
  );
};

export default EsmeTable;

